import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Chart, ChartEvent, LegendElement, LegendItem } from 'chart.js';
import { UIChart } from 'primeng/chart';
import { Skeleton } from 'primeng/skeleton';
import { Subject, takeUntil } from 'rxjs';
import { ThemeService } from '../../../../services/theme.service';

import {
  MixedChartDataSetsInterface,
  MixedChartDateRangeInterface,
  MixedChartInterface,
  MixedChartOptionsInterface,
} from '../../../../types/interfaces/charts.interface';

@Component({
  selector: 'app-chart-mixed',
  standalone: true,
  imports: [
    UIChart,
    Skeleton,

  ],
  templateUrl: './chart-mixed.component.html',
  styleUrl: './chart-mixed.component.css',
})
export class ChartMixedComponent implements OnChanges, AfterViewInit {
  @Input()
  public data!: MixedChartInterface;

  @Input()
  public options!: MixedChartOptionsInterface;

  @Input()
  public enableTimeRangeSelector: boolean = false;

  @Output()
  public timeRangeChange: EventEmitter<MixedChartDateRangeInterface> =
    new EventEmitter<MixedChartDateRangeInterface>();
  public chartOptions!: MixedChartOptionsInterface;
  @ViewChild(UIChart)
  private _chartComponent?: UIChart;
  private _hiddenDatasetIndexes: Set<number> = new Set<number>();

  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) private readonly _platformId: object,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _themeService: ThemeService,
  ) {
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.updateChartOptions();
    }

    if (changes['data']) {
      queueMicrotask(() => {
        this.applyHiddenDatasets();
      });
    }
  }

  public ngAfterViewInit(): void {
    this._themeService.theme$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateChartOptions();
      });

    queueMicrotask(() => {
      this.applyHiddenDatasets();
    });
  }

  private updateChartOptions(): void {
    if (!this.options) return;

    const textColor: string = this.getThemeTextColor();

    this.chartOptions = {
      ...this.options,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        ...this.options.plugins,
        legend: {
          ...this.options.plugins.legend,
          labels: {
            ...this.options.plugins.legend.labels,
            color: textColor,
          },
          onClick: (
            event: ChartEvent,
            legendItem: LegendItem,
            legend: LegendElement<'bar' | 'line'>,
          ) => this.onLegendClick(event, legendItem, legend),
        },
      },
      scales: {
        ...this.options.scales,
        x: {
          ...this.options.scales.x,
          ticks: {
            ...this.options.scales.x.ticks,
            color: textColor,
          },
        },
        y: {
          ...this.options.scales.y,
          ticks: {
            ...this.options.scales.y.ticks,
            color: textColor,
          },
        },
        y1: this.options.scales.y1
          ? {
            ...this.options.scales.y1,
            ticks: {
              ...this.options.scales.y1.ticks,
              color: textColor,
            },
          }
          : undefined,
      },
    };

    this._changeDetectorRef.markForCheck();

    queueMicrotask(() => {
      const chart: Chart<'bar' | 'line'> | null = this.getChart();

      if (!chart) {
        return;
      }

      chart.options = this.chartOptions as unknown as Chart<'bar' | 'line'>['options'];
      chart.update();
    });
  }

  private onLegendClick(
    event: ChartEvent,
    legendItem: LegendItem,
    legend: LegendElement<'bar' | 'line'>,
  ): void {
    const chart: Chart<'bar' | 'line'> = legend.chart;
    const datasetIndex: number | undefined = legendItem.datasetIndex;

    if (datasetIndex === undefined) return;

    const nextVisible: boolean = !chart.isDatasetVisible(datasetIndex);

    chart.setDatasetVisibility(datasetIndex, nextVisible);

    if (nextVisible) {
      this._hiddenDatasetIndexes.delete(datasetIndex);
    } else {
      this._hiddenDatasetIndexes.add(datasetIndex);
    }

    this.updateAxisVisibility(chart);
    chart.update();

    void event;
  }

  private applyHiddenDatasets(): void {
    const chart: Chart<'bar' | 'line'> | null = this.getChart();

    if (!chart || !this.data) return;

    this.data.datasets.forEach((dataset: MixedChartDataSetsInterface, index: number) => {
      chart.setDatasetVisibility(index, !this._hiddenDatasetIndexes.has(index));
    });

    this.updateAxisVisibility(chart);
    chart.update();
  }

  private updateAxisVisibility(chart: Chart<'bar' | 'line'>): void {
    const firstVisible: boolean = chart.isDatasetVisible(0);
    const secondVisible: boolean = chart.isDatasetVisible(1);
    const options: MixedChartOptionsInterface =
      chart.options as unknown as MixedChartOptionsInterface;

    options.scales.y.display = firstVisible;

    if (options.scales.y1) {
      options.scales.y1.display = secondVisible;
      options.scales.y1.grid.drawOnChartArea = !firstVisible;
    }
  }

  private getChart(): Chart<'bar' | 'line'> | null {
    const chart: unknown = this._chartComponent?.chart;

    if (chart instanceof Chart) {
      return chart as Chart<'bar' | 'line'>;
    }

    return null;
  }

  private getThemeTextColor(): string {
    if (!isPlatformBrowser(this._platformId)) {
      return '#111827';
    }

    const bodyStyle: CSSStyleDeclaration = getComputedStyle(document.body);
    const rootStyle: CSSStyleDeclaration = getComputedStyle(document.documentElement);

    return this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--text-color',
        '--text-primary',
      ],
      document.body.getAttribute('data-theme') === 'dark'
        ? '#ffffff'
        : '#111827',
    );
  }

  private getCssVariable(
    bodyStyle: CSSStyleDeclaration,
    rootStyle: CSSStyleDeclaration,
    variableNames: string[],
    fallback: string,
  ): string {
    for (const variableName of variableNames) {
      const bodyValue: string = bodyStyle.getPropertyValue(variableName)
        .trim();

      if (bodyValue !== '') {
        return bodyValue;
      }

      const rootValue: string = rootStyle.getPropertyValue(variableName)
        .trim();

      if (rootValue !== '') {
        return rootValue;
      }
    }

    return fallback;
  }
}
